import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { services } from '../data/services';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import './Services.css';

/**
 * Services page showcasing all available services
 * Features a hero banner, service grid with staggered reveal, and CTA
 */
export const Services: React.FC = () => {
  useDocumentTitle('Services');
  const cardsContainerRef = useScrollReveal({ threshold: 0.1 });

  const getImage = (path: string) => {
    try {
      return require(`../assets/images/${path}`);
    } catch {
      return '';
    }
  };

  return (
    <div className="services-page page-enter">
      {/* Hero Banner */}
      <section className="services-hero">
        <div className="services-hero__content">
          <h1 className="services-hero__title">Our Services</h1>
          <p className="services-hero__subtitle">
            Painting, finish work, and ongoing care for homes and commercial spaces
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-section">
        <div className="services-container">
          <div className="services-grid" ref={cardsContainerRef}>
            {services.map((service, index) => (
              <Link
                key={service.id}
                to={`/services/${service.id}`}
                className="service-card"
                style={{
                  animationDelay: `${index * 100}ms`,
                }}
              >
                <div className="service-card__image-container">
                  <img
                    src={getImage(service.image)}
                    alt={service.title}
                    className="service-card__image"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="service-card__overlay">
                    <h3 className="service-card__title">{service.title}</h3>
                  </div>
                </div>
                <div className="service-card__body">
                  <div className="service-card__title-row">
                    <h3 className="service-card__title">{service.title}</h3>
                    <span className="service-card__arrow" aria-hidden="true">→</span>
                  </div>
                  <p className="service-card__description">{service.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="services-cta">
        <div className="services-container">
          <div className="services-cta__content">
            <h2 className="services-cta__heading">Not sure where to begin?</h2>
            <p className="services-cta__text">
              Tell us what you are working on. We will help clarify the scope and the right next step.
            </p>
            <Button asLink="/contact" variant="primary" size="lg">
              Start a Conversation
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

Services.displayName = 'Services';
